import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { listMessageHistory, MessageHistoryItem, MessageHistoryPage as HistoryPage, MessageHistoryParams } from '../api/messageHistory';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import PageState from '../components/common/PageState';
import Pagination from '../components/common/Pagination';
import MessagePreviewMockup from '../components/messages/MessagePreviewMockup';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../styles/common/Button.styled';
import { ConsoleInput, ConsoleSelect } from '../styles/common/input.styled';
import * as T from '../styles/common/table.styled';
import { statusColors } from '../styles/consoleTheme';
import { formatLocalDateInputValue, getLocalMonthAgoDateInputValue } from '../utils/date';
import { formatPhoneNumber } from '../utils/format';

type Filters = Pick<MessageHistoryParams, 'startDate' | 'endDate' | 'type' | 'deliveryStatus' | 'to'>;
const defaultFilters = (): Filters => {
  const today = new Date();
  return { startDate: getLocalMonthAgoDateInputValue(today), endDate: formatLocalDateInputValue(today), type: '', deliveryStatus: '', to: '' };
};
const pageSize = 20;

const FilterForm = styled.form`
  display: flex; align-items: flex-end; flex-wrap: wrap; gap: 8px;
`;
const FilterField = styled.label`
  display: flex; flex-direction: column; gap: 5px; color: ${({ theme }) => theme.color.text_color_200};
  font-size: 12px; font-weight: 500;
`;
const DateInput = styled(ConsoleInput)`width: 150px;`;
const TypeSelect = styled(ConsoleSelect)`width: 180px;`;
const StatusSelect = styled(ConsoleSelect)`width: 140px;`;
const NumberInput = styled(ConsoleInput)`width: 170px;`;
const FilterActions = styled.div`display: flex; gap: 8px;`;
const FilterError = styled.p`
  margin: 0; color: ${({ theme }) => theme.color.point_n_100}; font-size: 12px;
`;
const ContentCell = styled(T.Td)`max-width: 0;`;
const ContentLink = styled.button`
  display: block; max-width: 100%; padding: 0; border: 0; background: transparent;
  color: ${({ theme }) => statusColors[theme.name].info}; font: inherit; text-align: left;
  text-decoration: underline; text-underline-offset: 2px; cursor: pointer;
  overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
  &:hover { text-decoration-thickness: 2px; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 2px; }
`;

const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ko-KR');
};

const typeLabels: Record<string, string> = {
  SMS: 'SMS(단문메시지)', LMS: 'LMS(장문메시지)', ATA: 'ATA(알림톡)',
};
const typeLabel = (type?: string) => type ? typeLabels[type] || type : '—';

const deliveryLabel = (status?: string) => ({
  PENDING: '대기', SUCCEEDED: '성공', FAILED: '실패',
} as Record<string, string>)[status || ''] || status || '—';

const deliveryTone = (status?: string): 'success' | 'danger' | 'warning' | 'neutral' => {
  if (status === 'SUCCEEDED') return 'success';
  if (status === 'FAILED') return 'danger';
  if (status === 'PENDING') return 'warning';
  return 'neutral';
};

export default function MessageHistoryPage() {
  const { mode, basicAccessToken } = useAuth();
  const [initialFilters] = useState(defaultFilters);
  const [draft, setDraft] = useState<Filters>(initialFilters);
  const [applied, setApplied] = useState<Filters>(initialFilters);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState<HistoryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filterError, setFilterError] = useState('');
  const [previewItem, setPreviewItem] = useState<MessageHistoryItem | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (mode !== 'basic' || !basicAccessToken) return;
    const id = ++requestId.current;
    setLoading(true);
    setLoadError(false);
    const params: MessageHistoryParams = {
      pg: page, limit: pageSize,
      ...(applied.startDate && { startDate: applied.startDate }),
      ...(applied.endDate && { endDate: applied.endDate }),
      ...(applied.type && { type: applied.type }),
      ...(applied.deliveryStatus && { deliveryStatus: applied.deliveryStatus }),
      ...(applied.to && { to: applied.to }),
    };
    listMessageHistory(params)
      .then(data => { if (requestId.current === id) setResult(data); })
      .catch(() => { if (requestId.current === id) setLoadError(true); })
      .finally(() => { if (requestId.current === id) setLoading(false); });
    return () => { requestId.current += 1; };
  }, [mode, basicAccessToken, page, applied, reloadKey]);

  const updateDraft = (key: keyof Filters, value: string) => {
    setDraft(current => ({ ...current, [key]: value }));
    setFilterError('');
  };
  const search = (event: React.FormEvent) => {
    event.preventDefault();
    if (draft.startDate && draft.endDate && draft.startDate > draft.endDate) {
      setFilterError('종료일은 시작일 이후여야 합니다.');
      return;
    }
    const next = { ...draft, type: draft.type?.trim(), to: draft.to?.trim() };
    setPage(1);
    setApplied(next);
  };
  const reset = () => {
    const defaults = defaultFilters();
    setDraft(defaults);
    setApplied(defaults);
    setPage(1);
    setFilterError('');
    setReloadKey(key => key + 1);
  };

  if (mode !== 'basic' || !basicAccessToken) return <Navigate to="/statistics/calls" replace />;

  return <T.PageContainer>
    <PageHeader title="발송 이력" count={!loading && !loadError ? result?.totalElements : undefined} />
    <FilterForm onSubmit={search}>
      <FilterField>시작일<DateInput type="date" value={draft.startDate} onChange={event => updateDraft('startDate', event.target.value)} /></FilterField>
      <FilterField>종료일<DateInput type="date" value={draft.endDate} onChange={event => updateDraft('endDate', event.target.value)} /></FilterField>
      <FilterField>메시지 유형<TypeSelect value={draft.type} onChange={event => updateDraft('type', event.target.value)}>
        <option value="">전체</option><option value="SMS">SMS(단문메시지)</option><option value="LMS">LMS(장문메시지)</option><option value="ATA">ATA(알림톡)</option>
      </TypeSelect></FilterField>
      <FilterField>전달 상태<StatusSelect value={draft.deliveryStatus} onChange={event => updateDraft('deliveryStatus', event.target.value)}>
        <option value="">전체</option><option value="PENDING">대기</option><option value="SUCCEEDED">성공</option><option value="FAILED">실패</option>
      </StatusSelect></FilterField>
      <FilterField>수신 번호<NumberInput placeholder="수신 번호" value={draft.to} onChange={event => updateDraft('to', event.target.value)} /></FilterField>
      <FilterActions><Button type="submit" $variant="secondary">검색</Button><Button type="button" $variant="ghost" onClick={reset}>초기화</Button></FilterActions>
    </FilterForm>
    {filterError && <FilterError role="alert">{filterError}</FilterError>}

    {loading ? <PageState status="loading" /> : loadError ? <PageState status="error" onRetry={() => setReloadKey(key => key + 1)} /> : result && <>
      {result.content.length ? <T.TableArea $minWidth={1080}>
        <T.Table>
          <thead><tr><T.Th $width="18%">생성일</T.Th><T.Th $width="19%">유형</T.Th><T.Th $width="14%">수신 번호</T.Th><T.Th $width="13%">발신 번호</T.Th><T.Th $width="27%">내용</T.Th><T.Th $width="9%">전달 상태</T.Th></tr></thead>
          <tbody>{result.content.map(item => <T.Tr key={item.id}>
            <T.Td>{formatDate(item.createdAt)}</T.Td>
            <T.Td>{typeLabel(item.actualType || item.requestedType)}</T.Td>
            <T.Td title={formatPhoneNumber(item.toNumber)}>{formatPhoneNumber(item.toNumber)}</T.Td>
            <T.Td title={item.fromNumber ? formatPhoneNumber(item.fromNumber) : undefined}>{item.fromNumber ? formatPhoneNumber(item.fromNumber) : '—'}</T.Td>
            <ContentCell title={item.renderedText}>{item.renderedText ? <ContentLink type="button" onClick={() => setPreviewItem(item)}>{item.renderedText}</ContentLink> : '—'}</ContentCell>
            <T.Td><T.Badge $tone={deliveryTone(item.deliveryStatus)} title={item.deliveryStatus}>{deliveryLabel(item.deliveryStatus)}</T.Badge></T.Td>
          </T.Tr>)}</tbody>
        </T.Table>
      </T.TableArea> : <PageState status="empty" title={applied.type || applied.deliveryStatus || applied.to ? '검색 결과가 없습니다.' : applied.startDate || applied.endDate ? '해당 기간에 발송 이력이 없습니다.' : '발송 이력이 없습니다.'} />}
      <T.TableFooter>
        <T.TotalCount>총 <span>{result.totalElements.toLocaleString()}</span>건</T.TotalCount>
        <Pagination currentPage={page} totalPages={result.totalPages} onPageChange={setPage} />
      </T.TableFooter>
    </>}
    {previewItem && <Modal title="발송 내용" onClose={() => setPreviewItem(null)} historyMode="none" width={(previewItem.actualType || previewItem.requestedType) === 'ATA' ? 680 : 520}>
      <MessagePreviewMockup
        variant={(previewItem.actualType || previewItem.requestedType) === 'ATA' ? 'kakao' : 'sms'}
        text={previewItem.renderedText}
        sender={previewItem.fromNumber ? formatPhoneNumber(previewItem.fromNumber) : undefined}
        sentAt={previewItem.createdAt}
      />
    </Modal>}
  </T.PageContainer>;
}
